import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTheme } from "@emotion/react";
import { Close } from "@mui/icons-material";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { useMediaQuery } from "@mui/system";
import vegaEmbed from "vega-embed";

export const VegaChart = ({ spec, width = 240, height = 160, title }) => {
  const chartRef = useRef(null);
  const popupChartRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [isDialogMounted, setIsDialogMounted] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const chartOptions = useMemo(
    () => ({
      renderer: "svg",
      theme: "light",
      actions: false,
    }),
    []
  );

  const popupChartOptions = useMemo(
    () => ({
      renderer: "svg",
      theme: "light",
      actions: true,
    }),
    []
  );

  const scaledSpec = useMemo(
    () => ({
      ...spec,
      width,
      height,
      autosize: {
        type: "fit",
        contains: "content",
      },
      config: {
        ...spec.config,
        view: {
          ...spec.config?.view,
          strokeWidth: 0,
          padding: 0,
        },
        axis: {
          ...spec.config?.axis,
          labelFontSize: 11,
          titleFontSize: 13,
        },
      },
    }),
    [spec, width, height]
  );

  const popupChartSpec = useMemo(
    () => ({
      ...spec,
      ...(isMobile
        ? {
            width: "container",
            height: "container",
            autosize: {
              type: "fit",
              contains: "padding",
              resize: true,
            },
          }
        : {
            autosize: {
              type: "fit",
              contains: "content",
            },
          }),
      config: {
        ...spec.config,
        view: {
          ...spec.config?.view,
          strokeWidth: 0,
          padding: isMobile ? 10 : 20,
        },
        axis: {
          ...spec.config?.axis,
          labelFontSize: isMobile ? 10 : 12,
          titleFontSize: isMobile ? 11 : 14,
        },
        legend: {
          ...spec.config?.legend,
          labelFontSize: isMobile ? 10 : 12,
          titleFontSize: isMobile ? 11 : 13,
        },
      },
    }),
    [spec, isMobile]
  );

  const renderChart = useCallback((ref, spec, options) => {
    if (ref.current) {
      ref.current.innerHTML = ""; // Efficiently clear the container
      vegaEmbed(ref.current, spec, options);
    }
  }, []);

  const dialogContent = useMemo(
    () => (
      <DialogContent
        sx={{
          p: isMobile ? 1 : 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div
          ref={popupChartRef}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "auto",
            maxWidth: "100%",
            maxHeight: "100%",
          }}
        />
      </DialogContent>
    ),
    [isMobile]
  );

  const dialogComponent = useMemo(
    () => (
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            ...(isMobile
              ? {}
              : {
                  minWidth: "900px",
                  minHeight: "600px",
                }),
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: isMobile ? 1 : 2,
            pb: 1,
            minHeight: "auto",
          }}
        >
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => setOpen(false)}
            aria-label="close"
            size={isMobile ? "small" : "medium"}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        {dialogContent}
      </Dialog>
    ),
    [open, isMobile, dialogContent]
  );

  const chartComponent = useMemo(
    () => (
      <div
        ref={chartRef}
        style={{
          width: "100%",
          height: `${height}px`,
          cursor: "pointer",
          overflow: "hidden",
          margin: "0 auto",
        }}
        onClick={() => setOpen(true)}
      />
    ),
    [height]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      renderChart(chartRef, scaledSpec, chartOptions);
    }, 100);
    return () => clearTimeout(timer);
  }, [scaledSpec, renderChart, chartOptions]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        setIsDialogMounted(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setIsDialogMounted(false);
    }
  }, [open]);

  useEffect(() => {
    if (isDialogMounted && popupChartRef.current) {
      const timer = setTimeout(() => {
        renderChart(popupChartRef, popupChartSpec, popupChartOptions);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isDialogMounted, popupChartSpec, renderChart, popupChartOptions]);

  return (
    <>
      {chartComponent}
      {dialogComponent}
    </>
  );
};
